import React, { useEffect, useState, useMemo } from 'react'
import { Message } from '@cx/ui'
import InfoRoundedIcon from '@mui/icons-material/InfoRounded'
import { TextField, Box, Stack, RadioGroup, FormControlLabel, Radio, styled, alpha } from '@mui/material'
import { postApplyInvoice } from '@/api'
import { Controller, useForm } from 'react-hook-form'
import { formatGoodsDetails } from './OrderCard'
import Modal2 from '@/components/Modal2'
import professional_invoice from '@/asset/img/professional_invoice.png'
import regular_invoice from '@/asset/img/regular_invoice.png'
import ConfirmInvoiceModal from './ConfirmInvoiceModal'
import { getPublicAccount } from '@/api/orders'
import { Decimal } from 'decimal.js'

interface ApplyInvoiceModalProps {
  open: boolean
  data: any
  onCancel: () => void
  onOk: () => void
}

function numToChinese(n: number) {
  const fraction = ['角', '分']
  const digit = ['零', '壹', '贰', '叁', '肆', '伍', '陆', '柒', '捌', '玖']
  const unit = [
    ['元', '万', '亿'],
    ['', '拾', '佰', '仟'],
  ]
  let head = n < 0 ? '负' : ''
  n = Math.abs(n)

  let s = ''

  // 处理角分
  for (let i = 0; i < fraction.length; i++) {
    s += (digit[Math.floor(n * 10 * Math.pow(10, i)) % 10] + fraction[i]).replace(/零./, '')
  }
  s = s || '整'
  n = Math.floor(n)

  // 处理整数部分
  for (let i = 0; i < unit[0].length && n > 0; i++) {
    let p = ''
    for (let j = 0; j < unit[1].length && n > 0; j++) {
      p = digit[n % 10] + unit[1][j] + p
      n = Math.floor(n / 10)
    }
    s = p.replace(/(零.)*零$/, '').replace(/^$/, '零') + unit[0][i] + s
  }

  return (
    head +
    s
      .replace(/(零.)*零元/, '元')
      .replace(/(零.)+/g, '零')
      .replace(/^整$/, '零元整')
  )
}

const InvoiceText = styled('div')(({ theme }) => ({
  position: 'absolute',
  color: 'rgba(0,0,0,0.65)',
  fontSize: 12,
  transform: 'scale(0.8)',
  transformOrigin: 'center left',
}))

const ApplyInvoiceModal: React.FC<ApplyInvoiceModalProps> = ({ open, onCancel, onOk, data }) => {
  const [visible, setVisible] = useState(false)
  const [company, setCompany] = useState<Record<string, any>>({})
  const [formValues, setFormValues] = useState<any>({})

  const {
    control,
    formState: { errors },
    handleSubmit,
    trigger,
    reset,
    watch,
  } = useForm({
    defaultValues: {
      invoice_type: '1',
      unit_name: '',
      taxpayer_id: '',
      unit_address: '',
      bank_name: '',
      bank_account: '',
      phone: '',
    },
  })

  const watchName = watch('unit_name')
  const watchType = watch('invoice_type')
  const watchTaxpayerId = watch('taxpayer_id')

  const isSpecialInvoice = watchType === '2'

  const onSubmit = handleSubmit((data) => {
    setFormValues(data)
    setVisible(true)
  })

  const onSubmitInvoice = async () => {
    return postApplyInvoice({ ...formValues, order_code: data.order_code }).then(() => {
      Message.success('申请成功')
      onOk()
      onCancel()
      setVisible(false)
    })
  }

  useEffect(() => {
    if (!open) {
      reset()
    }
  }, [open])

  const goodsDetail = useMemo(() => {
    return formatGoodsDetails(data?.goods_detail || '')
  }, [data])

  useEffect(() => {
    if (Object.keys(errors).length > 0) {
      trigger()
    }
  }, [watchType])

  useEffect(() => {
    if (data?.order_code) {
      getPublicAccount({ order_code: data?.order_code }).then((res) => {
        if (res) {
          setCompany(res)
        }
      })
    }
  }, [data])

  return (
    <Modal2 title='申请发票' width={806} open={open} onCancel={onCancel} onOk={onSubmit}>
      <ConfirmInvoiceModal
        open={visible}
        isSpecialInvoice={isSpecialInvoice}
        data={formValues}
        onCancel={() => {
          setVisible(false)
        }}
        onOk={onSubmitInvoice}
      />
      <Stack
        direction='row'
        gap={1}
        sx={(theme) => {
          return {
            py: 1,
            px: 2,
            backgroundColor: alpha(theme.palette.primary.main, 0.1),
            fontSize: 14,
            alignItems: 'center',
            marginBottom: 2,
            borderRadius: 1,
          }
        }}
      >
        <InfoRoundedIcon sx={{ color: 'primary.main', fontSize: 20 }} />
        <Box>审批(1-5个工作日)通过后可在订单页面自行下载</Box>
      </Stack>
      <Stack gap={3}>
        <Controller
          name='invoice_type'
          control={control}
          render={({ field }) => (
            <RadioGroup {...field} row>
              <FormControlLabel value='1' control={<Radio size='small' />} label='普通发票' />
              <FormControlLabel value='2' control={<Radio size='small' />} label='增值税专用发票' />
            </RadioGroup>
          )}
        />
        <Stack direction='row' gap={3}>
          <Controller
            rules={{ required: '请输入名称' }}
            name='unit_name'
            control={control}
            render={({ field }) => (
              <TextField
                required
                {...field}
                variant='outlined'
                placeholder='请输入名称'
                label='名称'
                fullWidth
                error={Boolean(errors.unit_name)}
                helperText={errors.unit_name?.message as string}
                size='small'
                autoComplete='off'
              />
            )}
          />
          <Controller
            rules={{ required: '请输入统一社会信用代码 / 纳税人识别号' }}
            name='taxpayer_id'
            control={control}
            render={({ field }) => (
              <TextField
                required
                {...field}
                variant='outlined'
                placeholder='请输入统一社会信用代码 / 纳税人识别号'
                label='统一社会信用代码 / 纳税人识别号'
                fullWidth
                error={Boolean(errors.taxpayer_id)}
                helperText={errors.taxpayer_id?.message as string}
                size='small'
                autoComplete='off'
              />
            )}
          />
        </Stack>
        <Controller
          rules={{ required: isSpecialInvoice ? '请输入单位地址' : false }}
          name='unit_address'
          control={control}
          render={({ field }) => (
            <TextField
              required={isSpecialInvoice}
              {...field}
              variant='outlined'
              placeholder='请输入单位地址'
              label='单位地址'
              fullWidth
              error={Boolean(errors.unit_address)}
              helperText={errors.unit_address?.message as string}
              size='small'
              autoComplete='off'
            />
          )}
        />
        <Stack direction='row' gap={3}>
          <Controller
            rules={{ required: isSpecialInvoice ? '请输入开户银行' : false }}
            name='bank_name'
            control={control}
            render={({ field }) => (
              <TextField
                required={isSpecialInvoice}
                {...field}
                variant='outlined'
                placeholder='请输入开户银行'
                label='开户银行'
                fullWidth
                error={Boolean(errors.bank_name)}
                helperText={errors.bank_name?.message as string}
                size='small'
                autoComplete='off'
              />
            )}
          />
          <Controller
            rules={{ required: isSpecialInvoice ? '请输入银行账户' : false }}
            name='bank_account'
            control={control}
            render={({ field }) => (
              <TextField
                required={isSpecialInvoice}
                {...field}
                variant='outlined'
                placeholder='请输入银行账户'
                label='银行账户'
                fullWidth
                error={Boolean(errors.bank_account)}
                helperText={errors.bank_account?.message as string}
                size='small'
                autoComplete='off'
              />
            )}
          />
        </Stack>
        {isSpecialInvoice && (
          <Controller
            rules={{ required: '请输入联系电话' }}
            name='phone'
            control={control}
            render={({ field }) => (
              <TextField
                required
                {...field}
                variant='outlined'
                placeholder='请输入联系电话'
                label='联系电话'
                sx={{ width: 'calc(50% - 12px)' }}
                error={Boolean(errors.phone)}
                helperText={errors.phone?.message as string}
                size='small'
                autoComplete='off'
              />
            )}
          />
        )}

        <Box sx={{ fontSize: 14 }}>发票预览</Box>
        <Box sx={{ position: 'relative' }}>
          <Box
            component='img'
            src={+watchType === 1 ? regular_invoice : professional_invoice}
            sx={{ width: '100%', border: '1px solid #eee', borderRadius: 1 }}
          />
          <InvoiceText sx={{ top: 131, left: 70 }}>{watchName}</InvoiceText>
          <InvoiceText sx={{ top: 131, left: 436 }}>{company.company}</InvoiceText>
          <InvoiceText sx={{ top: 151, left: 220 }}>{watchTaxpayerId}</InvoiceText>
          <InvoiceText sx={{ top: 151, left: 585 }}>91110105MA01KETL15</InvoiceText>
          <InvoiceText sx={{ top: 200, left: 18 }}>*研发和技术服务*技术服务费</InvoiceText>

          <InvoiceText sx={{ top: 200, left: 520 }}>
            {Decimal.mul(+(data?.real_amount || 0), 0.94).toFixed(2)}
          </InvoiceText>
          <InvoiceText sx={{ top: 200, left: 594 }}>6%</InvoiceText>
          <InvoiceText sx={{ top: 200, left: 684 }}>
            {Decimal.mul(+(data?.real_amount || 0), 0.06).toFixed(2)}
          </InvoiceText>

          <InvoiceText sx={{ top: 336, left: 516 }}>
            ￥{Decimal.mul(+(data?.real_amount || 0), 0.94).toFixed(2)}
          </InvoiceText>
          <InvoiceText sx={{ top: 336, left: 672 }}>
            ￥{Decimal.mul(+(data?.real_amount || 0), 0.06).toFixed(2)}
          </InvoiceText>
          <InvoiceText sx={{ top: 362, left: 206 }}>{numToChinese(data?.real_amount || 0)}</InvoiceText>
          <InvoiceText sx={{ top: 362, left: 554 }}>￥{data?.real_amount || 0}</InvoiceText>
          <InvoiceText sx={{ top: 386, left: 40 }}>
            百川云订单号：{data?.order_code} {goodsDetail?.map((i) => `${i?.name} - ${i?.value}`).join('、')}
          </InvoiceText>
        </Box>
      </Stack>
    </Modal2>
  )
}

export default ApplyInvoiceModal
